import {
  Directive,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  inject,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl, AbstractControl } from '@angular/forms';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { coerceBooleanProperty, BooleanInput } from '@angular/cdk/coercion';

/**
 * Width input type - either 'full' for 100% width or any CSS width value
 */
export type SgFieldWidth = 'full' | string;

/**
 * Abstract base class for form field components.
 * Provides common ControlValueAccessor implementation, focus monitoring,
 * and shared input properties for consistent form field behavior.
 *
 * @template T The type of value the form control holds (e.g., string, boolean, any)
 */
@Directive({ standalone: false })
export abstract class SgFormControlBase<T> implements ControlValueAccessor, OnDestroy {
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_invalid: BooleanInput;
  static ngAcceptInputType_required: BooleanInput;
  static ngAcceptInputType_clearable: BooleanInput;

  @Input() label: string = '';
  @Input() hint: string = '';
  @Input() error: string = '';
  @Input() prefixIcon: string = '';
  @Input() subscriptSizing: 'fixed' | 'dynamic' | 'none' = 'fixed';

  @Output() readonly focus = new EventEmitter<void>();
  @Output() readonly blur = new EventEmitter<void>();

  /**
   * Width of the form field.
   * - 'full': 100% width (fills container)
   * - Any CSS width value: '200px', '50%', '20rem', etc.
   * - undefined/empty: auto width (default)
   */
  @Input() width: SgFieldWidth | undefined;

  @HostBinding('class.sg-field--full-width')
  get hostFullWidthClass(): boolean {
    return this.width === 'full';
  }

  @HostBinding('style.width')
  get hostWidthStyle(): string | null {
    if (!this.width || this.width === 'full') {
      return null;
    }

    return this.width;
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _disabled = false;

  @Input()
  get invalid(): boolean {
    return this._invalid;
  }
  set invalid(value: BooleanInput) {
    this._invalid = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _invalid = false;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  protected _required = false;

  @Input()
  get clearable(): boolean {
    return this._clearable;
  }
  set clearable(value: BooleanInput) {
    this._clearable = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  protected _clearable = false;

  /**
   * The current value of the form control.
   * Exposed as @Input() to allow template-driven usage with [value] binding.
   */
  @Input()
  get value(): T | null {
    return this._value;
  }
  set value(val: T | null) {
    if (this._value !== val) {
      this._value = val;
      this.cdr.markForCheck();
    }
  }
  protected _value: T | null = null;

  focused = false;
  protected _touched = false;

  protected focusMonitor = inject(FocusMonitor);
  protected elementRef = inject(ElementRef);
  protected cdr = inject(ChangeDetectorRef);

  protected onChange: (value: T | null) => void = () => {};
  protected onTouched: () => void = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor.monitor(this.elementRef, true).subscribe((origin: FocusOrigin) => {
      const wasFocused = this.focused;
      this.focused = !!origin;

      if (wasFocused && !this.focused) {
        this.onTouched();
      }

      this.cdr.markForCheck();
    });
  }

  /**
   * Whether the label should float above the input.
   * Override in subclass if different logic is needed.
   */
  get shouldFloat(): boolean {
    return this.focused || !this.empty;
  }

  /**
   * Whether the control is empty.
   * Subclasses must implement this based on their value type.
   */
  abstract get empty(): boolean;

  /**
   * Whether to show the clear button.
   * Returns true if clearable is enabled, control is not empty, and not disabled.
   */
  get showClearButton(): boolean {
    return this._clearable && !this.empty && !this.disabled;
  }

  /**
   * Whether the control is required.
   * Checks both the explicit required input and form control validators.
   */
  get isRequired(): boolean {
    if (this._required) {
      return true;
    }

    if (this.ngControl?.control) {
      const control = this.ngControl.control;
      if (control.validator) {
        const validation = control.validator({} as AbstractControl);

        return validation !== null && validation['required'];
      }
    }

    return false;
  }

  /**
   * Whether the control should show invalid state.
   * Returns true if explicitly set to invalid OR if the form control is invalid and touched,
   * or if required validation fails in standalone mode (no ngControl).
   */
  get isInvalid(): boolean {
    if (this._invalid) {
      return true;
    }

    if (this.ngControl?.control) {
      return this.ngControl.control.invalid && this.ngControl.control.touched;
    }

    // Handle standalone required validation (no form control)
    if (this._required && this.empty && this._touched) {
      return true;
    }

    return false;
  }

  onFocus(): void {
    this.focused = true;
    this.focus.emit();
    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.focused = false;
    this._touched = true;
    this.onTouched();
    this.blur.emit();
    this.cdr.markForCheck();
  }

  // ControlValueAccessor implementation
  abstract writeValue(value: T | null): void;

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
  }
}
